(() => {
  if (window.__zicoMobileUXControllerLoaded) return;
  window.__zicoMobileUXControllerLoaded = true;

  class ZicoMobileUX {
    constructor() {
      this.root = document.documentElement;
      this.body = document.body;
      this.mobileMedia = window.matchMedia('(max-width: 989px)');
      this.categoryOpenedFromBottom = false;
      this.searchOpenedFromBottom = false;
      this.categoryFocusTarget = null;
      this.searchFocusTarget = null;
      this.cartSyncTimer = null;
      this.stickyHeight = 0;
      this.zaapiFrame = null;
      this.zaapiInsertionObserver = null;
      this.zaapiStyleObserver = null;
      this.keyboardOpen = false;
      this.keyboardBaselineHeight = 0;
      this.keyboardFrame = null;
      this.keyboardTimer = null;
    }

    init() {
      this.refreshNavControls();
      this.bindNavControls();
      this.syncCurrentNavState();
      this.bindCartEvents();
      this.bindZaapiController();
      this.bindVisualViewport();
      document.addEventListener('zico:sticky-atc-layout', (event) => this.onStickyLayout(event));
      document.addEventListener('shopify:section:load', () => this.onSectionLoad());
      window.addEventListener('pageshow', () => {
        this.syncCurrentNavState();
        this.scheduleCartCountSync();
        this.syncStickyLayoutFromDom();
      });
      this.syncCartCount();
      this.syncStickyLayoutFromDom();
      this.mobileMedia.addEventListener('change', () => this.applyZaapiLayout());
    }

    bindVisualViewport() {
      const viewport = window.visualViewport;
      if (!viewport) return;

      const schedule = () => this.scheduleKeyboardStateSync();
      viewport.addEventListener('resize', schedule, { passive: true });
      viewport.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('orientationchange', () => {
        this.keyboardBaselineHeight = 0;
        this.scheduleKeyboardStateSync(260);
      });
      document.addEventListener('focusin', (event) => {
        if (!this.isTextEntryControl(event.target)) return;
        this.keyboardBaselineHeight = Math.max(
          this.keyboardBaselineHeight,
          window.innerHeight,
          viewport.height + viewport.offsetTop
        );
        this.scheduleKeyboardStateSync(240);
      });
      document.addEventListener('focusout', () => this.scheduleKeyboardStateSync(180));
      this.scheduleKeyboardStateSync();
    }

    isTextEntryControl(element) {
      if (!(element instanceof HTMLElement)) return false;
      if (element.matches('textarea, select, [contenteditable="true"]')) return true;
      if (!element.matches('input')) return false;

      const type = (element.getAttribute('type') || 'text').toLowerCase();
      return !['button', 'checkbox', 'color', 'file', 'hidden', 'image', 'radio', 'range', 'reset', 'submit'].includes(
        type
      );
    }

    scheduleKeyboardStateSync(delay = 0) {
      window.cancelAnimationFrame(this.keyboardFrame);
      window.clearTimeout(this.keyboardTimer);
      const sync = () => {
        this.keyboardFrame = window.requestAnimationFrame(() => this.syncKeyboardState());
      };
      if (delay > 0) {
        this.keyboardTimer = window.setTimeout(sync, delay);
      } else {
        sync();
      }
    }

    syncKeyboardState() {
      const viewport = window.visualViewport;
      if (!viewport) return;

      const editing = this.isTextEntryControl(document.activeElement);
      const availableHeight = viewport.height + viewport.offsetTop;
      const baseline = Math.max(this.keyboardBaselineHeight, window.innerHeight, availableHeight);
      const keyboardOpen = this.mobileMedia.matches && editing && baseline - availableHeight > 120;
      const keyboardHeight = keyboardOpen ? Math.max(0, Math.round(baseline - availableHeight)) : 0;

      if (!editing && !keyboardOpen) this.keyboardBaselineHeight = Math.max(window.innerHeight, availableHeight);
      this.root.style.setProperty('--zico-keyboard-height', `${keyboardHeight}px`);
      if (keyboardOpen === this.keyboardOpen) return;

      this.keyboardOpen = keyboardOpen;
      this.body.classList.toggle('is-zico-keyboard-open', keyboardOpen);
      document.dispatchEvent(
        new CustomEvent('zico:keyboard-state', {
          detail: { open: keyboardOpen, height: keyboardHeight },
        })
      );
    }

    refreshNavControls() {
      this.nav = document.querySelector('.zico-mobile-nav');
      this.categoryTriggers = [...document.querySelectorAll('.zico-mobile-nav [data-zico-category-trigger]')];
      this.searchTriggers = [
        ...document.querySelectorAll(
          '.zico-mobile-nav [data-zico-search-trigger], .zico-mobile-nav a[href="/search"], .zico-mobile-nav a[href^="/search?"]'
        ),
      ];
      this.navCounts = [...document.querySelectorAll('.zico-mobile-nav [data-zico-cart-count]')];
    }

    getCurrentNavKey() {
      const path = window.location.pathname.replace(/\/+$/, '') || '/';
      if (path === '/') return 'home';
      if (path === '/cart') return 'bag';
      if (path === '/search') return 'search';
      if (path === '/pages/all-brands') return 'brand';
      if (path.startsWith('/collections')) return 'category';
      return '';
    }

    syncCurrentNavState() {
      const currentKey = this.getCurrentNavKey();
      document.querySelectorAll('.zico-mobile-nav [data-zico-nav-key]').forEach((link) => {
        const isCurrent = Boolean(currentKey) && link.dataset.zicoNavKey === currentKey;
        link.classList.toggle('is-current', isCurrent);
        if (isCurrent) {
          link.setAttribute('aria-current', 'page');
        } else {
          link.removeAttribute('aria-current');
        }
      });
    }

    bindNavControls() {
      this.categoryTriggers.forEach((trigger) => this.bindCategoryTrigger(trigger));
      this.searchTriggers.forEach((trigger) => this.bindSearchTrigger(trigger));
      this.bindCategoryDetails();
      this.bindSearchDetails();
    }

    bindCategoryTrigger(trigger) {
      if (trigger.dataset.zicoNativeBound === 'true') return;
      trigger.dataset.zicoNativeBound = 'true';
      trigger.addEventListener('click', (event) => {
        const summary = document.querySelector('header-drawer summary.header__icon--menu');
        if (!summary) return;

        event.preventDefault();
        const details = summary.closest('details');
        this.revealNativeHeader(summary);
        this.categoryOpenedFromBottom = true;
        this.categoryFocusTarget = trigger;
        this.bindCategoryDetails(details);

        if (!details?.open) summary.click();
        window.requestAnimationFrame(() => this.syncCategoryState(details));
      });
    }

    bindCategoryDetails(details = document.querySelector('header-drawer details')) {
      if (!details || details.dataset.zicoCategoryStateBound === 'true') return;
      details.dataset.zicoCategoryStateBound = 'true';
      details.addEventListener('toggle', () => {
        this.syncCategoryState(details);
        if (!details.open && this.categoryOpenedFromBottom) {
          this.restoreTriggerFocus(this.categoryFocusTarget);
          this.categoryOpenedFromBottom = false;
          this.categoryFocusTarget = null;
        }
      });
      this.syncCategoryState(details);
    }

    syncCategoryState(details = document.querySelector('header-drawer details')) {
      const expanded = Boolean(details?.open);
      this.categoryTriggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', String(expanded));
        trigger.classList.toggle('is-active', expanded);
      });
    }

    bindSearchTrigger(trigger) {
      if (trigger.dataset.zicoNativeBound === 'true') return;
      trigger.dataset.zicoNativeBound = 'true';
      trigger.addEventListener('click', (event) => {
        const summary = document.querySelector(
          '.header__search summary.header__icon--search, details-modal summary.header__icon--search, details-modal summary'
        );
        if (!summary) return;

        event.preventDefault();
        event.stopPropagation();
        const details = summary.closest('details');
        this.revealNativeHeader(summary);
        this.searchOpenedFromBottom = true;
        this.searchFocusTarget = trigger;
        this.bindSearchDetails(details);

        if (!details?.open) {
          const modal = summary.closest('details-modal');
          if (modal) modal.zicoReturnFocus = trigger;
          summary.click();
        } else {
          details.querySelector('input[type="search"]')?.focus({ preventScroll: true });
        }
        window.requestAnimationFrame(() => this.syncSearchState(details));
      });
    }

    bindSearchDetails(details = document.querySelector('details-modal details')) {
      if (!details || details.dataset.zicoSearchStateBound === 'true') return;
      details.dataset.zicoSearchStateBound = 'true';
      details.addEventListener('toggle', () => {
        this.syncSearchState(details);
        if (!details.open && this.searchOpenedFromBottom) {
          this.restoreTriggerFocus(this.searchFocusTarget);
          this.searchOpenedFromBottom = false;
          this.searchFocusTarget = null;
        }
      });
      this.syncSearchState(details);
    }

    syncSearchState(details = document.querySelector('details-modal details')) {
      const expanded = Boolean(details?.open);
      this.searchTriggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', String(expanded));
        trigger.classList.toggle('is-active', expanded);
      });
    }

    revealNativeHeader(summary) {
      const sectionHeader =
        summary.closest('.shopify-section-header, .section-header') ||
        document.querySelector('.shopify-section-header, .section-header');
      if (!sectionHeader) return;
      sectionHeader.classList.add('shopify-section-header-sticky');
      sectionHeader.classList.remove('shopify-section-header-hidden', 'animate');
    }

    restoreTriggerFocus(trigger) {
      const focusTrigger = () => {
        if (trigger?.isConnected) trigger.focus({ preventScroll: true });
      };

      focusTrigger();
      window.requestAnimationFrame(focusTrigger);
      window.setTimeout(focusTrigger, 120);
    }

    bindCartEvents() {
      document.addEventListener('cart:updated', () => this.scheduleCartCountSync());

      if (
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS.cartUpdate
      ) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.scheduleCartCountSync());
      }
    }

    bindZaapiController() {
      const frame = document.getElementById('zcw-iframe');
      if (frame) {
        this.attachZaapiFrame(frame);
        return;
      }

      if (this.zaapiInsertionObserver || !document.body) return;
      this.zaapiInsertionObserver = new MutationObserver(() => {
        const nextFrame = document.getElementById('zcw-iframe');
        if (!nextFrame) return;
        this.zaapiInsertionObserver.disconnect();
        this.zaapiInsertionObserver = null;
        this.attachZaapiFrame(nextFrame);
      });
      this.zaapiInsertionObserver.observe(document.body, { childList: true, subtree: true });
    }

    attachZaapiFrame(frame) {
      if (this.zaapiFrame === frame) {
        this.applyZaapiLayout();
        return;
      }

      this.zaapiStyleObserver?.disconnect();
      this.zaapiFrame = frame;
      this.applyZaapiLayout();
      this.zaapiStyleObserver = new MutationObserver(() => this.applyZaapiLayout());
      this.zaapiStyleObserver.observe(frame, { attributes: true, attributeFilter: ['style'] });
    }

    applyZaapiLayout() {
      const frame = this.zaapiFrame;
      if (!frame?.isConnected) {
        this.zaapiStyleObserver?.disconnect();
        this.zaapiStyleObserver = null;
        this.zaapiFrame = null;
        this.bindZaapiController();
        return;
      }

      const mobile = this.mobileMedia.matches;
      const frameBox = frame.getBoundingClientRect();
      const inlineWidth = frame.style.getPropertyValue('width').trim();
      const inlineHeight = frame.style.getPropertyValue('height').trim();
      const expandedMobile =
        mobile &&
        (inlineWidth.includes('100') ||
          inlineHeight.includes('100') ||
          frameBox.width >= window.innerWidth * 0.85 ||
          frameBox.height >= window.innerHeight * 0.85);
      const desired = {
        right: expandedMobile ? '0px' : mobile ? '1.2rem' : 'var(--zico-widget-desktop-offset)',
        bottom: expandedMobile ? '0px' : mobile ? 'var(--zico-widget-mobile-offset)' : 'var(--zico-widget-desktop-offset)',
        'z-index': 'var(--zico-layer-widget)',
      };

      Object.entries(desired).forEach(([property, value]) => {
        if (frame.style.getPropertyValue(property).trim() === value && frame.style.getPropertyPriority(property) === 'important') return;
        frame.style.setProperty(property, value, 'important');
      });
    }

    scheduleCartCountSync() {
      window.clearTimeout(this.cartSyncTimer);
      window.requestAnimationFrame(() => window.requestAnimationFrame(() => this.syncCartCount()));
      this.cartSyncTimer = window.setTimeout(() => this.syncCartCount(), 160);
    }

    syncCartCount() {
      const bubbleCount = document.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden="true"]');
      const count = bubbleCount ? Number.parseInt(bubbleCount.textContent.trim(), 10) || 0 : 0;

      this.navCounts.forEach((navCount) => {
        navCount.textContent = String(count);
        navCount.hidden = count < 1;
      });
    }

    onStickyLayout(event) {
      const height = Number(event.detail?.height);
      this.applyStickyHeight(Number.isFinite(height) && height > 0 ? height : 0);
    }

    syncStickyLayoutFromDom() {
      const sticky = document.querySelector('[data-zico-sticky-atc]:not([hidden])');
      const height = this.mobileMedia.matches && sticky ? Math.ceil(sticky.getBoundingClientRect().height) : 0;
      this.applyStickyHeight(height);
    }

    applyStickyHeight(height) {
      const nextHeight = Math.max(0, Math.ceil(height));
      if (nextHeight === this.stickyHeight) return;

      this.stickyHeight = nextHeight;
      this.root.style.setProperty('--zico-mobile-atc-height', `${nextHeight}px`);
      this.body.classList.toggle('has-zico-sticky-atc', nextHeight > 0);
    }

    onSectionLoad() {
      this.refreshNavControls();
      this.bindNavControls();
      this.scheduleCartCountSync();
      this.syncStickyLayoutFromDom();
      this.bindZaapiController();
    }
  }

  const boot = () => new ZicoMobileUX().init();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
